import { Component, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable } from 'rxjs';
import { BasicInputTypeEnum } from 'src/shared/enums/basic-input-type.enum';
import { IEventFilters, IFilterAction } from 'src/shared/interfaces/filters.interface';

import { BaseComponent } from '../../../abstracts/base.abstract';
import { featuresWithPermissions } from '../../../data/permissions';
import { ModalType } from '../../../enums';
import { EventTypeEnum } from '../../../enums/event-type.enum';
import { ExportTypesEnum } from '../../../enums/export-types.enum';
import { IBookedEventsByMonth } from '../../../interfaces/event.interface';
import { ISelectChoices } from '../../../interfaces/generic-select-config.interface';
import { ISchoolYear } from '../../../interfaces/groups/school-year.interface';
import { IBookedHomeworkSchool, IBookedHomeworkSchoolToCancel } from '../../../interfaces/homework-school.interface';
import { IBookedMeal, IMealSetting } from '../../../interfaces/meal.interface';
import { ModalService } from '../../../services';
import { ExportData } from '../../../store/actions/events.action';
import {
    CancelHomeworkSchoolBooking,
    GetBookedHomeworkSchool,
    GetTotalOfBookedHomeworkPerMonth,
    ResetFilteredBookedHomeworks,
} from '../../../store/actions/homework-school.action';
import { CancelMealBooking, GetBookedMealsPerMonth } from '../../../store/actions/meals.action';
import { AuthState } from '../../../store/state/auth.state';
import { EventsState } from '../../../store/state/events.state';
import { MealsState } from '../../../store/state/meals.state';
import { SchoolYearState } from '../../../store/state/school-year.state';
import {
    formatDateDDMMYYYY,
    formatDateYYYYMMDD,
    formatDateYYYYMMDDtoDDMMYYYY,
    getEndDateOfMonth, getFirstDayOfSchoolYear, getLastDayOfSchoolYear,
    getMonthTrad,
    getStartDateOfMonth,
    isDateRangeValid,
    parseDate,
} from '../../../utils/date.utils';
import { EntityTypeEnum } from './../../../enums/entity-type.enum';
import { HomeworkChoiceEnum } from './../../../enums/homework.enum';
import { PaymentStatusEnum } from './../../../enums/payment-status.enum';
import { IHomeworkSchoolSetting } from './../../../interfaces/homework-school.interface';
import { IBookedMealsToCancel } from './../../../interfaces/meal.interface';
import { IPartialUserData, IUser } from './../../../interfaces/users/user.interface';
import { GetTotalOfBookedMealsPerMonth } from './../../../store/actions/meals.action';
import { HomeworkSchoolState } from './../../../store/state/homework-school.state';

@Component({
    selector: 'app-events-history',
    templateUrl: './events-history.component.html',
    styleUrls: ['./events-history.component.scss'],
})
export class EventsHistoryComponent extends BaseComponent implements OnInit {
    @Select(EventsState.getCurrentEventType) public currentEventType$: Observable<EventTypeEnum>;
    @Select(SchoolYearState.getCurrentSchoolYear) public currentSchoolYear$: Observable<ISchoolYear>;
    @Select(AuthState.currentUser) public currentUser$: Observable<IUser>;
    @Select(AuthState.user) public user$: Observable<IUser>;
    @Select(AuthState.currentUserType) public currentUserType$: Observable<EntityTypeEnum>;
    @Select(AuthState.currentUserHasDivorcedParentsWithCustodyCalendar) public currentUserHasDivorcedParents$: Observable<boolean>;
    // meals
    @Select(MealsState.schoolMealsSettings) public mealsSettings$: Observable<IMealSetting[]>;
    @Select(MealsState.bookedMeals) public bookedMeals$: Observable<IBookedEventsByMonth[]>;
    @Select(MealsState.filteredBookedMeals) public filteredBookedMeals$: Observable<IBookedMeal[]>;
    //homeworks
    @Select(HomeworkSchoolState.bookedHomeworkSchool) public bookedHomeworks$: Observable<IBookedEventsByMonth[]>;
    @Select(HomeworkSchoolState.filteredBookedHomeworkSchool) public filteredBookedHomeworkSchool$: Observable<IBookedHomeworkSchool[]>;
    @Select(HomeworkSchoolState.schoolHomeworkSchoolSettings) public homeworkSchoolSettings$: Observable<IHomeworkSchoolSetting>;

    public basicInputTypeEnum = BasicInputTypeEnum;
    public currentMonthDetails: number;
    public bookedEvents: IBookedEventsByMonth[] = [];
    public filteredBookedEvents: IBookedEventsByMonth[] = [];
    public isDateRangeInError = false;
    public isFiltersApplied = false;

    public initStartDate: string;
    public initEndDate: string;

    public eventTypesEnum = EventTypeEnum;
    public currentEventType: EventTypeEnum;
    public entityTypeEnum = EntityTypeEnum;
    public currentEducationLevel = 0;
    public entityTypesEnum = EntityTypeEnum;
    public homeworkTypes = HomeworkChoiceEnum;
    public paymentStatus = PaymentStatusEnum;

    public currentSchoolYear: ISchoolYear;
    public schoolId: number = this.store.selectSnapshot(AuthState.currentSchoolId);

    public permissions = featuresWithPermissions;

    public mealsSettings: IMealSetting[];
    public homeworkSchoolSettings: IHomeworkSchoolSetting;
    public mealsSelectChoicesAll: ISelectChoices[] = [];
    public mealsSelectChoicesKindergarten: ISelectChoices[] = [];
    public mealsSelectChoicesPrimary: ISelectChoices[] = [];
    public educationLevels: ISelectChoices[] = [
        {
            label: 'SHOP.MATERNELLES',
            value: 2,
        },
        {
            label: 'SHOP.PRIMAIRES',
            value: 1,
        },
    ];
    public paymentStatusChoices: ISelectChoices[] = [
        {
            label: 'SHOP.WALLET.FILTERS.PAID',
            value: PaymentStatusEnum.PAID,
        },
        {
            label: 'SHOP.WALLET.FILTERS.NOT_PAID',
            value: PaymentStatusEnum.CREATED,
        },
    ];
    public homeworkTypesSelectChoices: ISelectChoices[] = [];
    public filterAction: IFilterAction[] = [
        { label: 'SHOP.EXPORT', action: (): void => this.exportData(), permission: this.permissions.shopping_export },
    ];

    public showFilters = false;
    public appliedFilters: IEventFilters;

    // GETTERS

    public get bookedMeals(): IBookedMeal[] {
        return this.bookedEvents.find((b: IBookedEventsByMonth) => b.monthNumber === this.currentMonthDetails - 1)?.bookedMeals || [];
    }

    public get bookedHomework(): IBookedHomeworkSchool[] {
        return (
            this.bookedEvents.find((b: IBookedEventsByMonth) => b.monthNumber === this.currentMonthDetails - 1)?.bookedHomeworkSchool || []
        );
    }

    public get startMonthIndex(): number {
        return this.appliedFilters?.dateStart
            ? parseDate(this.appliedFilters.dateStart).getMonth()
            : parseDate(this.initStartDate).getMonth();
    }

    public get endMonthIndex(): number {
        return this.appliedFilters?.dateEnd ? parseDate(this.appliedFilters.dateEnd).getMonth() : parseDate(this.initEndDate).getMonth();
    }

    public get mealChoices(): ISelectChoices[] {
        return this.currentEducationLevel === 0
            ? this.mealsSelectChoicesAll
            : this.currentEducationLevel === 1
            ? this.mealsSelectChoicesPrimary
            : this.mealsSelectChoicesKindergarten;
    }

    constructor(
        private store: Store,
        private translate: TranslateService,
        private modalService: ModalService,
        private deviceDetector: DeviceDetectorService
    ) {
        super();
    }

    ngOnInit(): void {
        this.initStartDate = formatDateYYYYMMDD(getFirstDayOfSchoolYear());
        this.initEndDate = formatDateYYYYMMDD(getLastDayOfSchoolYear());
        this.uns = this.currentSchoolYear$.subscribe((currentSchoolYear: ISchoolYear) => {
            if (currentSchoolYear) {
                this.currentSchoolYear = currentSchoolYear;
                this.initSubs();
            }
        });

        this.uns = this.homeworkSchoolSettings$.subscribe((settings: IHomeworkSchoolSetting) => {
            if (settings) {
                this.homeworkSchoolSettings = settings;
                this.homeworkTypesSelectChoices = [
                    {
                        label: settings.homework_name,
                        value: 0,
                    },
                    {
                        label: settings.reading_name,
                        value: 1,
                    },
                ];
            }
        });
    }

    public initSubs(): void {
        // get total of events per month based on event type (meal, homeworks)
        this.uns = this.currentEventType$.subscribe((currentEventType: EventTypeEnum) => {
            this.currentEventType = currentEventType;
            if (this.currentEventType === EventTypeEnum.MEAL) {
                this.store.dispatch(new GetTotalOfBookedMealsPerMonth());
            }
            if (this.currentEventType === EventTypeEnum.HOMEWORK) {
                this.store.dispatch(new GetTotalOfBookedHomeworkPerMonth());
            }
        });

        // init total of events per month - meals
        this.uns = this.bookedMeals$.subscribe((result: IBookedEventsByMonth[]) => {
            if (result) {
                this.bookedEvents = [...result];
            }
        });

        // init total of events per month - homeworks
        this.uns = this.bookedHomeworks$.subscribe((result: IBookedEventsByMonth[]) => {
            if (result) {
                this.bookedEvents = [...result];
            }
        });

        // init meal settings
        if (this.currentEventType && this.currentEventType === EventTypeEnum.MEAL) {
            this.uns = this.mealsSettings$.subscribe((mealsSettings: IMealSetting[]) => {
                this.mealsSettings = mealsSettings;
                mealsSettings.forEach((setting: IMealSetting) => {
                    // primary
                    if (setting.education_level_id === 1) {
                        this.mealsSelectChoicesPrimary = this.fillSelectChoices(setting);
                    }
                    // kindergarten
                    if (setting.education_level_id === 2) {
                        this.mealsSelectChoicesKindergarten = this.fillSelectChoices(setting);
                    }
                });
                const values: ISelectChoices[] = [...this.mealsSelectChoicesKindergarten, ...this.mealsSelectChoicesPrimary];
                this.mealsSelectChoicesAll = values.filter(
                    (item: ISelectChoices, index: number) =>
                        values.findIndex((item2: ISelectChoices) => item.value === item2.value) === index
                );
            });
        }
    }

    public showMonthDetails(i: number): void {
        if (this.currentMonthDetails !== i) {
            this.currentMonthDetails = i;
            const schoolHasSwitchToNextYear: boolean = this.currentSchoolYear.start_year === new Date().getFullYear();
            const from: string = getStartDateOfMonth(i, schoolHasSwitchToNextYear);
            const to: string = getEndDateOfMonth(i, schoolHasSwitchToNextYear);
            if (this.currentEventType === EventTypeEnum.MEAL) {
                this.store.dispatch(new GetBookedMealsPerMonth({ from, to }));
            }
            if (this.currentEventType === EventTypeEnum.HOMEWORK) {
                this.store.dispatch(new GetBookedHomeworkSchool({ from, to }));
            }
        } else {
            this.currentMonthDetails = -1;
        }
    }

    public getMonthYear(monthIndex: number): number {
        return monthIndex < 7 ? this.currentSchoolYear.end_year : this.currentSchoolYear.start_year;
    }

    public getMonthTrad(month: number): string {
        return getMonthTrad(month - 1);
    }

    public exportData(): void {
        const start: string = this.appliedFilters?.dateStart || this.initStartDate;
        const end: string = this.appliedFilters?.dateEnd || this.initEndDate;
        let filenameAppender: string = '';
        if (start && end) {
            filenameAppender += ' - ' + formatDateYYYYMMDDtoDDMMYYYY(start) + ' - ' + formatDateYYYYMMDDtoDDMMYYYY(end);
        } else {
            filenameAppender += ' - ' + formatDateDDMMYYYY(new Date());
        }

        filenameAppender += '.csv';

        console.log(this.currentEventType);
        switch (this.currentEventType) {
            case EventTypeEnum.HOMEWORK:
                this.store.dispatch(
                    new ExportData({
                        dataType: ExportTypesEnum.HOMEWORKS,
                        fileName: this.translate.instant('SHOP.SHARED.EVENTS.EXPORT.FILENAMES.HOMEWORKS') + filenameAppender,
                        startDate: start ? start : '',
                        endDate: end ? end : '',
                        education_level_id: this.appliedFilters?.education?.length === 1 ? this.appliedFilters.education[0] : null,
                        search: this.appliedFilters?.search,
                        homework_type: this.appliedFilters?.homework?.length === 1 ? this.appliedFilters.homework[0] : null,
                        paid: this.appliedFilters?.payment?.length === 1 ? (this.appliedFilters?.payment[0] === PaymentStatusEnum.PAID ? 1 : 0) : null,
                    })
                );
                break;

            case EventTypeEnum.MEAL:
                this.store.dispatch(
                    new ExportData({
                        dataType: ExportTypesEnum.MEALS,
                        fileName: this.translate.instant('SHOP.SHARED.EVENTS.EXPORT.FILENAMES.MEALS') + filenameAppender,
                        startDate: start ? start : '',
                        endDate: end ? end : '',
                        education_level_id: this.appliedFilters?.education?.length === 1 ? this.appliedFilters.education[0] : null,
                        search: this.appliedFilters?.search,
                        paid: this.appliedFilters?.payment?.length === 1 ? (this.appliedFilters?.payment[0] === PaymentStatusEnum.PAID ? 1 : 0) : null,
                        meal_choices: this.appliedFilters?.meals,
                    })
                );
                break;
        }
    }

    public monthComparison(monthIndex: number): boolean {
        if (!this.isFiltersApplied) {
            return true;
        } else {
            if (this.startMonthIndex >= 7 && this.endMonthIndex >= 7) {
                return monthIndex >= this.startMonthIndex && monthIndex <= this.endMonthIndex;
            }
            if (this.startMonthIndex >= 7 && this.endMonthIndex < 7) {
                return monthIndex >= this.startMonthIndex || monthIndex <= this.endMonthIndex;
            }
            if (this.startMonthIndex < 7 && this.endMonthIndex < 7) {
                return monthIndex >= this.startMonthIndex && monthIndex <= this.endMonthIndex;
            }
        }
        return false;
    }

    public openPopupDeleteMealOrder(orderId: number): void {
        this.modalService.openModal({
            type: ModalType.STANDARD,
            body: {
                title: 'SHOP.SHARED.DELETE_ORDER_TITLE',
                text: 'SHOP.SHARED.DELETE_ORDER_PARA',
                cancelButtonLabel: 'SHOP.SHARED.EVENTS.CANCEL_BTN',
                validateButtonLabel: 'SHOP.SHARED.EVENTS.CONFIRM_BTN',
                validateButtonFn: () => this.deleteMealOrder(orderId),
            },
        });
    }

    public openPopupViewParent(parent: IPartialUserData): void {
        this.modalService.openModal({
            type: ModalType.STANDARD,
            body: {
                text: `${parent.firstname} ${parent.lastname}`,
            },
        });
    }

    public openPopupDeleteHomeworkOrder(orderId: number): void {
        this.modalService.openModal({
            type: ModalType.STANDARD,
            body: {
                title: 'SHOP.SHARED.DELETE_ORDER_TITLE',
                text: 'SHOP.SHARED.DELETE_ORDER_PARA',
                cancelButtonLabel: 'SHOP.SHARED.EVENTS.CANCEL_BTN',
                validateButtonLabel: 'SHOP.SHARED.EVENTS.CONFIRM_BTN',
                validateButtonFn: () => this.deleteHomeworkOrder(orderId),
            },
        });
    }

    public deleteMealOrder(orderId: number): void {
        const cancelHomeworkSchool: IBookedMealsToCancel = {
            book_meal_ids: [orderId],
        };
        this.store.dispatch(new CancelMealBooking(cancelHomeworkSchool));
    }

    public deleteHomeworkOrder(orderId: number): void {
        const cancelHomeworkSchool: IBookedHomeworkSchoolToCancel = {
            book_homework_ids: [orderId],
        };
        this.store.dispatch(new CancelHomeworkSchoolBooking(cancelHomeworkSchool));
    }

    public applyFiltersEvent(event: IEventFilters): void {
        this.appliedFilters = event;

        const from: string = event.dateStart || null;
        const to: string = event.dateEnd || null;
        const search: string = event.search || null;
        const educationLevelId: number[] = event.education || [];
        const homeworkTypes: number[] = event.homework || [];
        const meals: string[] = event.meals || [];

        if (!this.filtersHasValues(event)) {
            this.isFiltersApplied = false;
            return;
        }

        if (isDateRangeValid(from, to)) {
            this.isFiltersApplied = true;
            if (this.currentEventType === EventTypeEnum.MEAL) {
                const mealChoices: string[] = meals || null;
                this.store.dispatch(
                    new GetBookedMealsPerMonth(
                        {
                            from,
                            to,
                            search,
                            mealChoices: mealChoices.length > 0 ? mealChoices : null,
                            educationLevelId: educationLevelId.length > 1 ? null : educationLevelId[0],
                            paymentStatus: event.payment,
                        },
                        true
                    )
                );
            }
            if (this.currentEventType === EventTypeEnum.HOMEWORK) {
                this.store.dispatch(new ResetFilteredBookedHomeworks());
                this.store.dispatch(
                    new GetBookedHomeworkSchool(
                        {
                            from,
                            to,
                            search,
                            educationLevelId: educationLevelId.length > 1 ? null : educationLevelId[0],
                            homeworkTypes: homeworkTypes.length > 1 ? null : homeworkTypes[0],
                            paymentStatus: event.payment,
                        },
                        true
                    )
                );
            }
        }
    }

    public filtersHasValues(filters: IEventFilters): boolean {
        return (
            (filters.dateStart && filters.dateStart !== this.initStartDate) ||
            (filters.dateEnd && filters.dateEnd !== this.initEndDate) ||
            filters.search.length > 0 ||
            filters.meals?.length > 0 ||
            filters.education?.length > 0 ||
            filters.payment?.length > 0 ||
            filters.homework?.length > 0
        );
    }

    private fillSelectChoices(currentSetting: IMealSetting): ISelectChoices[] {
        let choices: ISelectChoices[] = [];
        if (currentSetting.soup) {
            choices = [...choices, { value: 'soup', label: this.translate.instant('ENUM.MEAL-CHOICES.SOUP') }];
        }
        if (currentSetting.toast) {
            choices = [...choices, { value: 'toast', label: this.translate.instant('ENUM.MEAL-CHOICES.TOAST') }];
        }
        if (currentSetting.hot_meal) {
            choices = [...choices, { value: 'hot_meal', label: this.translate.instant('ENUM.MEAL-CHOICES.HOT_MEAL') }];
        }
        return choices;
    }
}
